import type { Worksheet } from "exceljs";
import type { Department } from "../../../contexts/AuthProvider";
import type { DepartmentLocation, NurseInShift, PrintoutSchedule } from "../../../utils/models";
import type { CustomColumnNamesConfig, DailyPrintoutGrouping, LayoutShape, PrintoutColumn, PrintoutColumnType, RowNoteFlags, SectionPlan } from "../types";
import { emitBlankDataCells, emitBlankHeaderCells } from "./emptyColumns";
import { renderPivotSectionHeader } from "./pivotSectionHeader";
import { renderPivotSectionRows } from "./pivotSectionRows";
import { calculateJobGroupStats, calculateRoleGroupStats, emitRoleGroupStatRows } from "./roleGroupStats";
import { emitColumnHeaders, emitSectionTitle } from "./sectionHeader";
import { renderStandardSectionRows } from "./standardSectionRows";
import { applyVerticalMerges } from "./verticalMerges";

interface RenderSectionArgs {
	worksheet: Worksheet;
	section: SectionPlan;
	currentRow: number;
	layout: LayoutShape;
	enabledColumns: PrintoutColumn[];
	rowGroupingBy: PrintoutColumnType[];
	printoutGroupBy: DailyPrintoutGrouping;
	hospitalData: PrintoutSchedule;
	department: Department;
	timezone: string;
	jobManagementEnabled: boolean;
	printNotes: boolean;
	staffPerRow: number;
	showRoleGroupStats: boolean;
	departmentLocationsRequired: DepartmentLocation[];
	censusByLocationName: Map<string, number>;
	/** Headings for custom Start Time group keys; null when custom shift time groups are off. */
	customTimeGroupLabels: ReadonlyMap<string, string> | null;
	dayAbbr: string;
	noteFlags: RowNoteFlags | undefined;
	sortBy: PrintoutColumn[] | undefined;
	customStringOrderMap: Map<string, number> | null | undefined;
	mergeSimilarShiftTimes: boolean | undefined;
	columnGroupingBy: PrintoutColumnType | null;
	appendRoleToName: boolean;
	customColumnNames: CustomColumnNamesConfig;
	/** See `RenderSettings.hideColumnNames`. */
	hideColumnNames: boolean;
	/** See `RenderSettings.appendEmptyColumns` — already validated by `renderSheets`. */
	appendEmptyColumns: number;
}

type PivotLayoutShape = Extract<LayoutShape, { kind: "pivot" }>;
type StandardLayoutShape = Extract<LayoutShape, { kind: "standard" }>;

/**
 * Pivot section: section header (title + bands + optional sub-column names) → pivoted data rows →
 * blank cells for any appended columns after the last band.
 */
const renderPivotSection = (args: RenderSectionArgs, layout: PivotLayoutShape): number => {
	const { worksheet, section, columnGroupingBy, appendEmptyColumns } = args;
	const dataStart = renderPivotSectionHeader({
		worksheet,
		groupingKey: section.groupingKey,
		currentRow: args.currentRow,
		grouping: args.printoutGroupBy,
		anchorColumns: layout.layout.anchorColumns,
		subColumns: layout.layout.subColumns,
		horizontalValues: layout.layout.horizontalValues,
		horizontalValueLabels: layout.layout.horizontalValueLabels,
		department: args.department,
		jobManagementEnabled: args.jobManagementEnabled,
		columnGroupingBy,
		censusByLocationName: args.censusByLocationName,
		customTimeGroupLabels: args.customTimeGroupLabels,
		customColumnNames: args.customColumnNames,
		hideColumnNames: args.hideColumnNames,
		appendEmptyColumns,
	});
	if (!columnGroupingBy) return dataStart; // shouldn't happen — pivot requires columnGroupingBy
	const nextRow = renderPivotSectionRows({
		worksheet,
		rows: section.rows,
		currentRow: dataStart,
		columnGroupingBy,
		layout: layout.layout,
		enabledColumns: args.enabledColumns,
		rowGroupingBy: args.rowGroupingBy,
		hospitalData: args.hospitalData,
		department: args.department,
		timezone: args.timezone,
		jobManagementEnabled: args.jobManagementEnabled,
		sortBy: args.sortBy,
		customStringOrderMap: args.customStringOrderMap,
		mergeSimilarShiftTimes: args.mergeSimilarShiftTimes,
		noteFlags: args.noteFlags,
		appendRoleToName: args.appendRoleToName,
	});
	emitBlankDataCells({ worksheet, startRow: dataStart, endRow: nextRow - 1, firstColumn: layout.flatColumns.length + 1, count: appendEmptyColumns });
	return nextRow;
};

/**
 * Role/job-group stats for a section: count ALL of the section's nurses (incl. those with no location),
 * and sum required over the section's config locations (each carrying its census-resolved
 * amountRequired). Mirrors the daily view's per-group stats.
 */
const emitSectionStats = (args: RenderSectionArgs, currentRow: number, totalColumns: number): number => {
	const { worksheet, section, jobManagementEnabled, departmentLocationsRequired, department, hospitalData } = args;
	const sectionNurses: NurseInShift[] = section.rows.map((r) => r.nurse).filter((n): n is NurseInShift => !!n);
	const stats = jobManagementEnabled
		? calculateJobGroupStats({ nurses: sectionNurses, departmentLocations: departmentLocationsRequired, department })
		: calculateRoleGroupStats({ nurses: sectionNurses, departmentLocations: departmentLocationsRequired, department, hospitalData });
	return stats.length ? emitRoleGroupStatRows({ worksheet, currentRow, stats, totalColumns }) : currentRow;
};

/**
 * Standard section: title → optional role-group stats → column headers (unless hidden) → packed data
 * rows → vertical merges for `rowGroupingBy` columns → blank cells for any appended columns, which sit
 * after the last real column (after the trailing Notes column when there is one, and once at the far
 * right however many staff slots a row packs).
 */
const renderStandardSection = (args: RenderSectionArgs, layout: StandardLayoutShape): number => {
	const { worksheet, section, printNotes, jobManagementEnabled, rowGroupingBy, appendEmptyColumns } = args;
	const hasNotesInColumns = layout.expandedColumns.some((col) => col.type === "notes");
	const realColumns = layout.expandedColumns.length + (printNotes && !hasNotesInColumns ? 1 : 0);
	const firstBlankColumn = realColumns + 1;
	const totalColumns = realColumns + appendEmptyColumns;

	let currentRow = emitSectionTitle({
		worksheet,
		groupingKey: section.groupingKey,
		currentRow: args.currentRow,
		grouping: args.printoutGroupBy,
		totalColumns,
		department: args.department,
		jobManagementEnabled,
		censusByLocationName: args.censusByLocationName,
		customTimeGroupLabels: args.customTimeGroupLabels,
	});

	if (args.showRoleGroupStats) currentRow = emitSectionStats(args, currentRow, totalColumns);

	if (!args.hideColumnNames) {
		const headerRow = currentRow;
		currentRow = emitColumnHeaders({
			worksheet,
			currentRow,
			expandedColumns: layout.expandedColumns,
			printNotes,
			jobManagementEnabled,
			customColumnNames: args.customColumnNames,
		});
		emitBlankHeaderCells({ worksheet, row: headerRow, firstColumn: firstBlankColumn, count: appendEmptyColumns });
	}

	const dataStart = currentRow;
	currentRow = renderStandardSectionRows({
		worksheet,
		rows: section.rows,
		currentRow,
		enabledColumns: args.enabledColumns,
		expandedColumns: layout.expandedColumns,
		hospitalData: args.hospitalData,
		department: args.department,
		timezone: args.timezone,
		jobManagementEnabled,
		printNotes,
		staffPerRow: args.staffPerRow,
		rowGroupingBy,
		noteFlags: args.noteFlags,
		sortBy: args.sortBy,
		customStringOrderMap: args.customStringOrderMap,
		appendRoleToName: args.appendRoleToName,
	});
	applyVerticalMerges({ worksheet, startRow: dataStart, endRow: currentRow - 1, expandedColumns: layout.expandedColumns, rowGroupingBy });
	emitBlankDataCells({ worksheet, startRow: dataStart, endRow: currentRow - 1, firstColumn: firstBlankColumn, count: appendEmptyColumns });

	return currentRow;
};

/**
 * Render one section into the worksheet (mapping §4 phase 6 / §6.* / §8.* / §9.2), dispatching by
 * `layout.kind` to the standard or pivot flow above.
 *
 * Returns the row after the section.
 */
export const renderSection = (args: RenderSectionArgs): number =>
	args.layout.kind === "pivot" ? renderPivotSection(args, args.layout) : renderStandardSection(args, args.layout);
